import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useFocusData, useHomeRole } from '../home/useHomeData';
import { buildRecentTasks, workLogsFromCache, type RecentTask } from './timerModel';

/**
 * "המשימות האחרונות שלי" for the top of a task picker (work-log form, timer
 * dialog), from data the client already has: the timer status (shared with
 * GlobalTimer), the "המיקוד שלי" list (same query as Home) and any work logs
 * already in the query cache. No new endpoint.
 */
export function useMyRecentTasks(limit = 6): { items: RecentTask[]; isLoading: boolean } {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const { isStaff } = useHomeRole();
  const focus = useFocusData();
  const timerQ = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: isStaff,
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const timers = timerQ.data?.data?.timers;
  const openRecords = timerQ.data?.data?.open_records;
  const items = useMemo(() => buildRecentTasks({
    timers,
    openRecords,
    workLogs: workLogsFromCache(queryClient.getQueriesData({ queryKey: ['work-logs'] }).map(([, d]) => d)),
    focus: focus.items,
    userId,
    limit,
  }), [timers, openRecords, focus.items, userId, limit, queryClient]);

  return { items, isLoading: focus.isLoading && items.length === 0 };
}
